<script setup lang="ts">
/**
 * Ce que comprend chaque niveau (écran 9, tableau « Deux niveaux »,
 * S:5803-5813) : chaque fonction, oui ou non au Basique, oui à l'option
 * Avancé, puis les conditions de l'offre. Aucun prix : le tarif est
 * communiqué par l'interlocuteur commercial.
 *
 * Les lignes et leur niveau sont ceux de la maquette (`OFFRE`, champ
 * `client`, S:5726-5743, dans l'ordre de `ordreOffre`), le niveau de chaque
 * ligne étant celui de ses gestes dans la matrice des accès v1.6.
 */
// ================================
// IMPORTS
// ================================
import { useI18n } from "vue-i18n"
import { NIVEAUX_DES_FONCTIONS } from "@modules/alarmes-v2/utils/niveaux"

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n()
</script>

<template>
    <!-- ================================ -->
    <!-- COMPOSANT PRINCIPAL -->
    <!-- ================================ -->
    <section
        class="rounded-lg border border-surface-200 bg-surface-0"
        data-test="tableau-niveaux"
    >
        <header class="border-b border-surface-200 px-4 py-3">
            <h2 class="text-base font-semibold text-surface-900">
                {{ t("alarmesV2.option.niveaux.titre") }}
            </h2>
            <p class="text-xs text-surface-600">
                {{ t("alarmesV2.option.niveaux.sousTitre") }}
            </p>
        </header>
        <div class="overflow-x-auto px-4 py-3">
            <table class="w-full text-left text-sm">
                <thead class="text-xs text-surface-600">
                    <tr>
                        <th scope="col" class="py-2 pr-4 font-medium">
                            {{
                                t("alarmesV2.option.niveaux.colonnes.fonction")
                            }}
                        </th>
                        <th scope="col" class="py-2 pr-4 font-medium">
                            {{ t("alarmesV2.option.niveaux.colonnes.basique") }}
                        </th>
                        <th scope="col" class="py-2 font-medium">
                            {{ t("alarmesV2.option.niveaux.colonnes.avance") }}
                        </th>
                    </tr>
                </thead>
                <tbody>
                    <tr
                        v-for="ligne in NIVEAUX_DES_FONCTIONS"
                        :key="ligne.cle"
                        class="border-t border-surface-100"
                        :data-test="`niveau-${ligne.cle}`"
                    >
                        <td class="py-2 pr-4 text-surface-800">
                            {{
                                t(
                                    `alarmesV2.option.niveaux.lignes.${ligne.cle}`
                                )
                            }}
                        </td>
                        <td
                            class="py-2 pr-4"
                            :class="
                                ligne.basique
                                    ? 'font-medium text-green-700'
                                    : 'text-surface-500'
                            "
                            data-test="basique"
                        >
                            {{
                                ligne.basique
                                    ? t("alarmesV2.option.niveaux.oui")
                                    : t("alarmesV2.option.niveaux.non")
                            }}
                        </td>
                        <td class="py-2 font-medium text-green-700">
                            {{ t("alarmesV2.option.niveaux.oui") }}
                        </td>
                    </tr>
                </tbody>
            </table>
            <dl
                class="mt-4 grid grid-cols-1 gap-x-4 gap-y-1 text-sm sm:grid-cols-[max-content_1fr]"
            >
                <dt class="font-medium text-surface-800">
                    {{ t("alarmesV2.option.niveaux.basiqueTerme") }}
                </dt>
                <dd class="text-surface-700">
                    {{ t("alarmesV2.option.niveaux.basiqueTexte") }}
                </dd>
                <dt class="font-medium text-surface-800">
                    {{ t("alarmesV2.option.niveaux.avanceTerme") }}
                </dt>
                <dd class="text-surface-700">
                    {{ t("alarmesV2.option.niveaux.avanceTexte") }}
                    <span class="text-surface-500">{{
                        t("alarmesV2.option.niveaux.tarif")
                    }}</span>
                </dd>
                <dt class="font-medium text-surface-800">
                    {{ t("alarmesV2.option.niveaux.smsTerme") }}
                </dt>
                <dd class="text-surface-700">
                    {{ t("alarmesV2.option.niveaux.smsTexte") }}
                    <span class="text-surface-500">{{
                        t("alarmesV2.option.niveaux.tarif")
                    }}</span>
                </dd>
                <dt class="font-medium text-surface-800">
                    {{ t("alarmesV2.option.niveaux.essaiTerme") }}
                </dt>
                <dd class="text-surface-700">
                    {{ t("alarmesV2.option.niveaux.essaiTexte") }}
                </dd>
            </dl>
        </div>
    </section>
</template>
